import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { queryClient } from '../components/queryClient';
import { useFleet } from '../hooks/useFleet';
import { useHasPermission } from '../hooks/useHasPermission';
import { PermittedPlate } from '../interfaces';
import { PermittedPlatesService } from '../services';

const key = 'permittedPlates';

export const usePermittedPlates = () => {
  const fleet = useFleet();
  const permission = useHasPermission('fleets:read:whitelist_plates');
  const queryKey = [key, fleet.uuid];

  const queryFn = () => PermittedPlatesService.get(fleet.uuid);

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.uuid && permission,
    defaltData: [],
  });
};

export const useOldPermittedList = (): PermittedPlate[] => {
  const fleet = useFleet();
  const oldPermittedList = fleet.whitelist_plates || [];
  const oldPermittedListInNewFormat: PermittedPlate[] = oldPermittedList.map((plate) => ({
    categorization_code: plate.group as string,
    license_plate: plate.plate,
    state_code: plate.state,
    permitted_plate_id: plate.state + plate.plate,
    created_by: 'system',
    event_type_identifier: '*',
    match_mode: 'exact' as const,
  }));
  return oldPermittedListInNewFormat;
};

export const useBackwardsCompatiblePlates = () => {
  const oldPlates = useOldPermittedList();
  const permission = useHasPermission('fleets:read:whitelist_plates');

  const fleet = useFleet();
  const queryKey = [key, 'backwardsCompatible', fleet.uuid];

  const queryFn = async () => {
    const res = await PermittedPlatesService.get(fleet.uuid);
    if (res.length === 0) {
      return oldPlates;
    }
    return res;
  };

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.uuid && permission,
    defaltData: [],
  });
};

export const usePermittedPlatesGroups = () => {
  const { data } = usePermittedPlates();
  const [uniqueGroups, setUniqueGroups] = useState<string[]>([]);

  useEffect(() => {
    const groups = data?.map((p) => p.categorization_code) ?? [];
    const newUniqueGroups: string[] = [...new Set(groups)];
    setUniqueGroups(newUniqueGroups);
  }, [data?.length]);

  return uniqueGroups;
};

export const invalidatePermittedPlates = (fleetId: number | string) => {
  queryClient.invalidateQueries([key, fleetId]);
  queryClient.invalidateQueries([key, 'backwardsCompatible', fleetId]);
};
